import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useRef, useState } from 'react';
import {
	View,
	TouchableOpacity,
	StyleSheet,
	TextInput,
	Keyboard,
	Pressable
} from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addCustomerNotes } from '../../../redux/slices/newSellscreenSlice';
import { CustomModal } from '../../../shared/commonComponents/customModal';

export default function CustomerNoteSection() {
	const inputRef = useRef<TextInput>(null)
	const notes = useAppSelector((state) => state.NewSellscreenSlice.customerNotes)
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const dispatch = useAppDispatch()
	const [tempNote, setTempNote] = useState('');
	const [modalVisible, setModalVisible] = useState(false);

	const handleOpen = () => {
		setTempNote(notes[locationId]);
		setModalVisible(true);
	};

	const handleSave = () => {
		dispatch(addCustomerNotes({ notes: tempNote, locationId }))
		setModalVisible(false);
		setTempNote("");
	};

	const handleDelete = () => {
		dispatch(addCustomerNotes({ notes: '', locationId }))
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.container}>

						{notes[locationId] ? (
							<View style={styles.filledCard}>

								<TouchableOpacity
									style={styles.noteContent}
									onPress={handleOpen}
									activeOpacity={0.7}
								>
									<ZenText content={"📝"} style={styles.filledIcon} />
									<View style={styles.textWrapper}>
										<ZenText content={"Customer Note:"} style={styles.noteLabel} />
										<ZenText content={notes[locationId]} style={styles.actualNoteText} numberOfLines={2} />
									</View>
								</TouchableOpacity>

								<TouchableOpacity
									style={styles.deleteBtn}
									onPress={handleDelete}
									hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
								>
									<ZenIcon name='close' type='antdesign' color={theme?.red500} size={14} />
								</TouchableOpacity>
							</View>
						) : (
							<TouchableOpacity style={styles.emptyButton} onPress={handleOpen}>
								<ZenText content={"📝"} style={styles.emptyIcon} />
								<ZenText content={"Add Customer Notes"} style={styles.emptyText} />
							</TouchableOpacity>
						)}

						<CustomModal keyboardView visible={modalVisible} onShow={() => { inputRef?.current?.focus() }} onRequestClose={() => setModalVisible(false)} transparent animationType="fade">
							<TouchableOpacity
								style={styles.modalOverlay}
								activeOpacity={1}
								onPress={() => Keyboard.dismiss()}
							>
								<Pressable style={StyleSheet.absoluteFill} onPress={() => setModalVisible(false)} />
								<View style={styles.modalContent}>
									<ZenText content={"Customer Note"} style={styles.modalTitle} />
									<TextInput
										ref={inputRef}
										style={styles.inputBox}
										placeholder="e.g. Allergy to peanuts, delivers to back door..."
										placeholderTextColor={theme?.grey}
										value={tempNote}
										onChangeText={setTempNote}
										multiline
										autoFocus
									/>
									<View style={styles.modalActions}>
										<TouchableOpacity onPress={() => setModalVisible(false)} style={styles.cancelBtn}>
											<ZenText content={"Cancel"} style={styles.cancelText} />
										</TouchableOpacity>
										<TouchableOpacity onPress={handleSave} style={styles.saveBtn}>
											<ZenText content={"Save Note"} style={styles.saveText} />
										</TouchableOpacity>
									</View>
								</View>
							</TouchableOpacity>
						</CustomModal>

					</View>
				)
			}}
		</ThemeConsumer>
	);
}

const style = (theme: Theme) => (
	StyleSheet.create({
		container: { marginBottom: 16 },

		emptyButton: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			paddingVertical: 12,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			borderRadius: 12,
			borderStyle: 'dashed',
			backgroundColor: theme?.iceWhite
		},
		emptyIcon: { marginRight: 8, fontSize: theme?.smallHeaderFontSize, opacity: 0.6 },
		emptyText: { fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.grey },

		filledCard: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			backgroundColor: theme?.warmCream,
			borderRadius: 12,
			padding: 12,
			borderWidth: 1,
			borderColor: theme?.warmCream,
			borderLeftWidth: 4,
			borderLeftColor: theme?.goldenOrange,
		},
		noteContent: {
			flex: 1,
			flexDirection: 'row',
			alignItems: 'flex-start',
			marginRight: 10,
		},
		filledIcon: {
			fontSize: theme?.smallerFontSize,
			marginRight: 10,
			marginTop: 2,
		},
		textWrapper: {
			flex: 1,
		},
		noteLabel: {
			fontSize: theme?.miniLabel,
			fontFamily: theme?.bold,
			color: theme?.goldenOrange,
			textTransform: 'uppercase',
			marginBottom: 2,
		},
		actualNoteText: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			lineHeight: 18,
		},
		deleteBtn: {
			padding: 8,
			backgroundColor: 'rgba(0,0,0,0.05)',
			borderRadius: 8,
		},

		modalOverlay: {
			flex: 1,
			backgroundColor: theme?.modalBg100,
			justifyContent: 'center',
			alignItems: "center",
			padding: 20,
		},
		modalContent: {
			width: "60%",
			backgroundColor: theme?.light,
			borderRadius: 16,
			padding: 20,
		},
		modalTitle: { fontSize: theme?.smallerFontSize, fontFamily: theme?.bold, marginBottom: 12 },
		inputBox: {
			backgroundColor: theme?.softSlate,
			borderRadius: 8,
			padding: 12,
			height: 100,
			textAlignVertical: 'top',
			fontSize: theme?.smallHeaderFontSize,
			color: theme?.lightDark,
			fontFamily: theme?.medium,
			marginBottom: 16,
		},
		modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
		cancelBtn: { padding: 10 },
		cancelText: { color: theme?.grey, fontFamily: theme?.medium, fontSize: theme?.errorFontSize },
		saveBtn: { backgroundColor: theme?.primary, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
		saveText: { color: theme?.light, fontFamily: theme?.bold, fontSize: theme?.errorFontSize },
	})
)